import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');

export default function InsightsTeaser() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/blog/posts`, { params: { limit: 3 } })
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => {});
  }, []);

  if (!posts.length) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="home-insights-section">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-semibold inline-flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" /> FuturePath Insights
          </p>
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-white mt-3">
            Read the guides behind the platform
          </h2>
          <p className="text-base text-slate-300 mt-3 max-w-2xl">
            Honest, practical writing on careers, college, skilled trades, scholarships, money and how AI is
            reshaping the work your kids will actually do.
          </p>
        </div>
        <Link to="/insights" data-testid="home-insights-all-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 hover:text-cyan-200 transition-colors duration-200 whitespace-nowrap">
          Visit the blog <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
        {posts.map((p, i) => (
          <motion.div key={p.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}>
            <Link to={`/insights/${p.slug}`} data-testid={`home-post-${p.slug}`}
              className="group flex h-full flex-col rounded-2xl glass-tint glass-cyan overflow-hidden">
              {p.featured_image && (
                <div className="h-44 overflow-hidden">
                  <img src={p.featured_image} alt={p.featured_image_alt || p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-5 flex flex-col flex-1">
                <span className="text-[11px] uppercase tracking-wider text-cyan-300 font-semibold">{p.category}</span>
                <h3 className="font-outfit text-lg font-semibold text-white mt-1.5 group-hover:text-cyan-200 transition-colors duration-200">
                  {p.title}
                </h3>
                {p.summary && <p className="text-sm text-slate-300 mt-2 line-clamp-3">{p.summary}</p>}
                <div className="flex items-center gap-3 mt-auto pt-4 text-[11px] text-slate-300">
                  <span>{fmt(p.published_at)}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.reading_time} min read</span>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
